import { router, usePage } from '@inertiajs/react'
import { createConsumer } from '@rails/actioncable'
import { useEffect, useState } from 'react'
import Layout from '../Layout'
import { isSoundMuted, playChime, setSoundMuted, unlockAudio } from '../../lib/sound'

interface KitchenOrderItem {
  name: string
  variant_name: string | null
  quantity: number
  notes: string | null
}

interface KitchenOrder {
  id: number
  sequential_number: number
  sequential_date: string | null
  account_id: number
  sale_id: number | null
  order_id: number | null
  source: string
  status: 'pending' | 'preparing' | 'ready' | 'served' | 'cancelled'
  consumption_type: 'dine_in' | 'takeaway' | 'delivery' | null
  items: KitchenOrderItem[]
  notes: string | null
  started_at: number | null
  completed_at: number | null
  created_at: string
  updated_at: string
}

interface Props {
  orders: KitchenOrder[]
  account: {
    id: number
    name: string
    slug: string
    tenant_type: string
  }
  from?: string | null
  to?: string | null
  show_all?: boolean
}

// Fecha local YYYY-MM-DD (ajustada por offset UTC para no cruzar de día)
function todayStr(): string {
  const d = new Date()
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  preparing: 'Preparando',
  ready: 'Listo',
  served: 'Entregada',
  cancelled: 'Cancelada',
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-400',
  preparing: 'bg-blue-500',
  ready: 'bg-green-500',
  served: 'bg-gray-300 text-gray-600',
  cancelled: 'bg-red-500',
}

// Tipo de consumo — lo PRIMERO que cocina debe ver: se prepara distinto si es
// para comer en el local, para llevar o delivery.
const CONSUMPTION_LABELS: Record<string, string> = {
  dine_in: '🍽️ Comer aquí',
  takeaway: '🥡 Para llevar',
  delivery: '🛵 Delivery',
}

const CONSUMPTION_COLORS: Record<string, string> = {
  dine_in: 'bg-gray-200 text-[#202020]',
  takeaway: 'bg-[#FFC107] text-[#202020]',
  delivery: 'bg-[#55ACEE] text-white',
}

function formatTimer(ms: number): string {
  const totalSecs = Math.floor(ms / 1000)
  const hours = Math.floor(totalSecs / 3600)
  const mins = Math.floor((totalSecs % 3600) / 60)
  const secs = totalSecs % 60
  const pad = (n: number) => n.toString().padStart(2, '0')
  if (hours > 0) return `${hours}:${pad(mins)}:${pad(secs)}`
  return `${pad(mins)}:${pad(secs)}`
}

export default function KitchenIndex({ orders: initialOrders, account, from: fromProp, to: toProp, show_all: showAllProp }: Props) {
  const [orders, setOrders] = useState<KitchenOrder[]>(initialOrders)
  const [subscription, setSubscription] = useState<any>(null)
  const [muted, setMuted] = useState<boolean>(isSoundMuted())
  const [from, setFrom] = useState<string>(fromProp || toProp || todayStr())
  const [to, setTo] = useState<string>(toProp || fromProp || todayStr())
  const [showAll, setShowAll] = useState<boolean>(!!showAllProp)
  // Force re-render every second for timers
  const [, setTick] = useState(0)

  const applyFilter = (fromVal: string, toVal: string, all: boolean) => {
    setShowAll(all)
    router.get(
      `/${account.slug}/kitchen`,
      all ? { all: '1' } : { from: fromVal, to: toVal },
      { preserveState: true, replace: true }
    )
  }

  const onFromChange = (v: string) => {
    setFrom(v)
    const effectiveTo = to < v ? v : to
    setTo(effectiveTo)
    if (v) applyFilter(v, effectiveTo, false)
  }

  const onToChange = (v: string) => {
    setTo(v)
    const effectiveFrom = v && from > v ? v : from
    setFrom(effectiveFrom)
    if (v) applyFilter(effectiveFrom, v, false)
  }

  // Desbloquear audio en el primer gesto del usuario (política de autoplay)
  useEffect(() => {
    const unlock = () => unlockAudio()
    window.addEventListener('pointerdown', unlock, { once: true })
    return () => window.removeEventListener('pointerdown', unlock)
  }, [])

  // Subscribe to ActionCable
  useEffect(() => {
    const consumer = createConsumer()
    const sub = consumer.subscriptions.create(
      { channel: 'KitchenChannel' },
      {
        received(data: any) {
          if (data.type === 'kitchen_order_created') {
            playChime()
          }
          if (data.type === 'kitchen_order_created' || data.type === 'kitchen_order_updated') {
            setOrders((prev) => {
              const idx = prev.findIndex((o) => o.id === data.order.id)
              if (idx >= 0) {
                const next = [...prev]
                next[idx] = data.order
                return next
              }
              return [data.order, ...prev]
            })
          }
        },
      }
    )
    setSubscription(sub)

    return () => {
      sub.unsubscribe()
      consumer.disconnect()
    }
  }, [])

  // Timer tick every second
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(interval)
  }, [])

  const advanceStatus = (ko: KitchenOrder, to: string) => {
    if (subscription) {
      subscription.send({ type: 'advance_status', id: ko.id, to })
    }
  }

  const getElapsed = (ko: KitchenOrder): number => {
    const start = new Date(ko.created_at).getTime()
    if (ko.status === 'served' && ko.completed_at) {
      return ko.completed_at - start
    }
    return Date.now() - start
  }

  return (
    <Layout account={account}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#202020]">Comandas</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const next = !muted
                setMuted(next)
                setSoundMuted(next)
              }}
              className="px-3 py-1.5 rounded border border-gray-300 bg-white text-sm font-medium text-[#202020] hover:bg-gray-100 transition-colors"
              title={muted ? 'Activar sonido' : 'Silenciar sonido'}
            >
              {muted ? '🔇' : '🔊'}
            </button>
            <span className="text-sm text-gray-500">
              {orders.filter((o) => o.status !== 'served' && o.status !== 'cancelled').length} activas
            </span>
          </div>
        </div>

        {/* Filtro de fechas: por defecto solo hoy; selector para ver días anteriores */}
        <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input
              type="date"
              value={from}
              onChange={(e) => onFromChange(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm text-[#202020]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input
              type="date"
              value={to}
              onChange={(e) => onToChange(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm text-[#202020]"
            />
          </div>
          <button
            onClick={() => {
              const t = todayStr()
              setFrom(t)
              setTo(t)
              applyFilter(t, t, false)
            }}
            className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
              !showAll && from === to ? 'bg-[#55ACEE] text-white border-[#55ACEE]' : 'bg-white text-[#202020] border-gray-300 hover:bg-gray-100'
            }`}
          >
            Hoy
          </button>
          <button
            onClick={() => applyFilter('', '', true)}
            className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
              showAll ? 'bg-[#55ACEE] text-white border-[#55ACEE]' : 'bg-white text-[#202020] border-gray-300 hover:bg-gray-100'
            }`}
          >
            Todo
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-lg">No hay comandas pendientes</p>
            <p className="text-sm mt-2">Las ventas y pedidos web aparecerán aquí automáticamente</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {orders.map((ko) => (
              <div
                key={ko.id}
                className={`bg-white rounded-lg shadow-md border overflow-hidden ${
                  ko.status === 'cancelled' ? 'opacity-60' : ''
                }`}
              >
                {/* Header */}
                <div className="px-4 py-3 border-b flex items-center justify-between bg-gray-50">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#202020]">#{ko.sequential_number}</span>
                    <span className="text-xs text-gray-500">
                      {ko.source === 'pos' ? 'POS' : 'Web'}
                      {ko.sequential_date ? ` · ${ko.sequential_date.split('-').reverse().join('/')}` : ''}
                    </span>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${
                      STATUS_COLORS[ko.status] || 'bg-gray-400'
                    }`}
                  >
                    {STATUS_LABELS[ko.status] || ko.status}
                  </span>
                </div>

                {/* Timer */}
                <div className="px-4 py-2 bg-[#202020] text-white text-center">
                  <span className="text-2xl font-mono font-bold tabular-nums">
                    {formatTimer(getElapsed(ko))}
                  </span>
                </div>

                {/* Tipo de consumo — banda destacada, lo primero que ve cocina */}
                {ko.consumption_type && (
                  <div
                    className={`px-4 py-1.5 text-center text-lg font-extrabold uppercase tracking-wide ${
                      CONSUMPTION_COLORS[ko.consumption_type] || 'bg-gray-200 text-[#202020]'
                    }`}
                  >
                    {CONSUMPTION_LABELS[ko.consumption_type] || ko.consumption_type}
                  </div>
                )}

                {/* Items */}
                <div className="px-4 py-3 space-y-2">
                  {ko.items && ko.items.length > 0 ? (
                    ko.items.map((item: KitchenOrderItem, idx: number) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <span className="bg-[#55ACEE] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                          {item.quantity}
                        </span>
                        <div>
                          <span className="text-[#202020] font-medium">{item.name}</span>
                          {item.variant_name && (
                            <span className="text-gray-500 ml-1">— {item.variant_name}</span>
                          )}
                          {item.notes && (
                            <p className="text-xs text-gray-400 mt-0.5">{item.notes}</p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400">Sin ítems</p>
                  )}
                </div>

                {/* Notes */}
                {ko.notes && (
                  <div className="px-4 py-2 border-t bg-yellow-50">
                    <p className="text-xs text-yellow-800">
                      <span className="font-semibold">Nota:</span> {ko.notes}
                    </p>
                  </div>
                )}

                {/* Action buttons */}
                {ko.status !== 'served' && ko.status !== 'cancelled' && (
                  <div className="px-4 py-3 border-t flex flex-wrap gap-2">
                    {ko.status === 'pending' && (
                      <button
                        onClick={() => advanceStatus(ko, 'preparing')}
                        className="flex-1 bg-[#55ACEE] text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-[#3b9ed6] transition-colors"
                      >
                        Preparar
                      </button>
                    )}
                    {ko.status === 'preparing' && (
                      <button
                        onClick={() => advanceStatus(ko, 'ready')}
                        className="flex-1 bg-green-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-green-600 transition-colors"
                      >
                        Listo
                      </button>
                    )}
                    {ko.status === 'ready' && (
                      <button
                        onClick={() => advanceStatus(ko, 'served')}
                        className="flex-1 bg-gray-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-gray-700 transition-colors"
                      >
                        Entregar
                      </button>
                    )}
                    <button
                      onClick={() => advanceStatus(ko, 'cancelled')}
                      className="bg-red-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-red-600 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}
